import {
    Box,
    Button,
    Grid,
    Skeleton,
    Theme,
    Typography
} from "@mui/material"
import {
    ChartIcon,
    CurrencyIcon,
    MoneyGrow,
    PercentageIcons,
} from "../../../../assets/SvgIcons"
import {
    DescriptiveAnalysisProps,
    InfoCardProps,
    InfoCardsData
} from "./types"
import { formatCurrency } from "../../../../utils/utilityFunctions"
import {
    useDispatch,
    useSelector
} from "react-redux"
import { useEffect, useState } from "react"
import { RootStateType } from "../../../../redux-store/reducers"
import { ZeroStateInvestment } from "../../../investorOverview/common/ZeroStateInvestment"
import PortfolioAllocation from "./portfolioAllocation"
import PortfolioHealth from "./portfolioHealth"
import AssetAllocation from "./assetAllocation"
import PortfolioExposure from "./portfolioExposure"
import Holdings from "./holdings"
import HoldingPeriodAnalysis from "./holdingPeriodAnalysis"
import { getTotalValues } from "../../../../redux-store/actions/analytics"
import { GetTotalValuesResponseBody } from "../../../../redux-store/types/analytics"
import AssetGroupData from "./assetGroupData"
import { DescriptiveAnalysisLoading } from "../../loadingComponents"


const initialInfoCardsDataArray = [
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <MoneyGrow {...props} viewBox="0 0 57 50" />,
        title: "Current Market Value",
        value: 0
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <CurrencyIcon {...props} />,
        title: "Invested Value",
        value: 0
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <ChartIcon {...props} viewBox="0 0 57 50" />,
        title: "Gain/Loss (%)",
        value: 0,
        percentage: 0,
        valueTextColor: 'success.800'
    },
    {
        Icon: (props: React.SVGProps<SVGSVGElement>) => <PercentageIcons {...props} viewBox="0 0 57 50" />,
        title: "Absolute Return",
        value: 0
    }
]

function InfoCard({
    Icon,
    title,
    value = 0,
    percentage = 0,
    valueTextColor = "text.primary",
    isLoading = false,
}: InfoCardProps) {
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const formatedPercentage = !Number.isNaN(Number(percentage ?? 0)) ?
        Math.round((percentage ?? 0) * 100) / 100 : '0'

    return (
        <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                borderRadius: "15px",
                boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                padding: "25px 18px",
                display: "flex",
                height: "100%",
                alignItems: "center",
            })}
        >
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <Icon />
            </Box>
            <Box
                sx={{
                    flex: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-end",
                    rowGap: "8px",
                    width: '80%',
                }}
            >
                {isLoading && <>
                    <Skeleton
                        animation="wave"
                        sx={{ width: '60%' }}
                    />
                    <Skeleton
                        animation="wave"
                        sx={{ width: '100%' }}
                    />
                </>}
                {!isLoading && (
                    <>
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "14px",
                                color: theme.palette.text.primary,
                                fontWeight: "500"
                            })}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="body_500"
                            sx={{
                                lineHeight: "27.5px",
                                color: valueTextColor
                            }}
                        >
                            {title === "Current Market Value" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Invested Value" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })}`
                            )}
                            {title === "Gain/Loss (%)" && (
                                (value ?? 0) < 0 ? `- ${formatCurrency({ value: Math.abs(value ?? 0), locale, currencyCode })} (${formatedPercentage}%)` : `${formatCurrency({ value: value ?? 0, locale, currencyCode })} (${formatedPercentage}%)`
                            )}
                            {title === "Absolute Return" && (
                                !Number.isNaN(Number(value ?? 0)) ?
                                    `${Math.round((value ?? 0) * 100) / 100} %` : '0 %'
                            )}
                        </Typography>
                    </>
                )}
            </Box>

        </Box>
    )
}

export default function DescriptiveAnalysis({
    clientCode,
    portfolioId,
}: DescriptiveAnalysisProps): JSX.Element {
    const dispatch = useDispatch();

    const [infoCardsDataLoading, setInfoCardsDataLoading] = useState<boolean>(false)
    const [infoCardsDataObj, setInfoCardsDataObj] = useState<{
        isSuccess: boolean;
        message: string;
        data: InfoCardsData[]
    }>({
        isSuccess: true,
        message: "",
        data: initialInfoCardsDataArray
    })

    const { iwappRmId = "" } = useSelector((state: RootStateType) => state.id);

    const fetchInfoCardsData = async (clientCode: string, portfolioId: string) => {
        try {
            setInfoCardsDataLoading(true)

            const payload = {
                rmId: `${iwappRmId}`,
                clientCode,
                portfolioId,
                assetGroup: null,
            }

            const response = await (dispatch(getTotalValues(payload)) as unknown as Promise<GetTotalValuesResponseBody>);

            setInfoCardsDataObj(prevState => ({
                ...prevState,
                isSuccess: response?.isSuccess ?? true,
                message: response?.message ?? "",
                data: prevState.data.map(obj => ({
                    ...obj,
                    ...((obj?.title === "Current Market Value") ? {
                        value: response?.result?.totalMarketValue ?? 0
                    } : {}),
                    ...((obj?.title === "Invested Value") ? {
                        value: response?.result?.totalInvestedValue ?? 0
                    } : {}),
                    ...((obj?.title === "Gain/Loss (%)") ? {
                        value: response?.result?.gainLoss ?? 0,
                        percentage: response?.result?.percentage ?? 0
                    } : {}),
                    ...((obj?.title === "Absolute Return") ? {
                        value: response?.result?.irr ?? 0,
                    } : {}),
                }))
            }))
        } catch (error) {
            console.log((error as Error)?.message);
        } finally {
            setInfoCardsDataLoading(false)
        }
    }




    useEffect(() => {
        fetchInfoCardsData(
            clientCode,
            portfolioId
        )
    }, [clientCode, portfolioId])

    const portfolioNotExistStatus = Number(infoCardsDataObj?.data?.find(obj => obj?.title === "Invested Value")?.value ?? 0) === 0

    if (infoCardsDataLoading) {
        return <DescriptiveAnalysisLoading />
    }

    if (!infoCardsDataLoading && portfolioNotExistStatus) {
        return <Box
            sx={(theme: Theme) => ({
                backgroundColor: theme.palette.common.white,
                borderRadius: "25px",
                padding: "25px 30px",
                height: "50vh"
            })}
        >
            <ZeroStateInvestment isSuccess={infoCardsDataObj?.isSuccess} />
        </Box>
    }

    return (
        <>
            {/* Info cards */}
            <Grid
                container
                spacing={2}
            >
                {infoCardsDataObj.data.map((obj: InfoCardsData) => (
                    <Grid
                        item
                        key={obj.title}
                        xs={12}
                        md={6}
                        lg={3}
                    >
                        <InfoCard
                            Icon={obj?.Icon}
                            title={obj?.title}
                            value={obj?.value}
                            percentage={obj?.percentage}
                            valueTextColor={obj?.valueTextColor}
                            isLoading={infoCardsDataLoading}
                        />
                    </Grid>
                ))}
            </Grid>

            {/* Portfolio Allocation and Portfolio Health */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "20px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px"
                }}
            >
                {/* Portfolio Allocation */}
                <Box
                    sx={(theme: Theme) => ({
                        flex: { xs: 1, lg: 2 },
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <PortfolioAllocation
                        clientCode={clientCode}
                        portfolioId={portfolioId}
                    />
                </Box>
                {/* Portfolio Health */}
                <Box
                    sx={(theme: Theme) => ({
                        flex: { xs: 1, lg: 3 },
                        borderRadius: "25px",
                        padding: "30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <PortfolioHealth
                        clientCode={clientCode}
                        portfolioId={portfolioId}
                    />
                </Box>
            </Box>

            {/* Asset Allocation and Portfolio Exposure */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "20px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px"
                }}
            >
                {/* Asset Allocation */}
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <AssetAllocation
                        clientCode={clientCode}
                        portfolioId={portfolioId}
                    />
                </Box>
                {/* Portfolio Exposure */}
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        borderRadius: "25px",
                        padding: "30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <PortfolioExposure
                        clientCode={clientCode}
                        portfolioId={portfolioId}
                    />
                </Box>
            </Box>

            {/* Holdings and Holding Period Analysis */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: 'column', lg: 'row' },
                    columnGap: { xs: "0px", lg: "20px" },
                    rowGap: { xs: "35px", lg: "0px" },
                    marginTop: "35px"
                }}
            >
                {/* Holdings */}
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        backgroundColor: theme.palette.common.white,
                        borderRadius: "20px",
                        padding: "30px",
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    })}
                >
                    <Holdings
                        clientCode={clientCode}
                        portfolioId={portfolioId}
                    />
                </Box>
                {/* Holding Period Analysis */}
                <Box
                    sx={(theme: Theme) => ({
                        flex: 1,
                        borderRadius: "25px",
                        padding: "30px",
                        backgroundColor: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    })}
                >
                    <HoldingPeriodAnalysis
                        clientCode={clientCode}
                        portfolioId={portfolioId}
                    />
                </Box>
            </Box>

            {/* Asset Group DATA dependent on Tabs */}
            <AssetGroupData
                clientCode={clientCode}
                portfolioId={portfolioId}
            />
        </>
    )
}